import React, { useState, useEffect, useRef } from 'react';
import Calendar from '../components/Calendar';
import EventFilters from '../components/EventFilters';
import SwipeableEvent from '../components/SwipeableEvent';
import ExpandableEventCard from '../components/ExpandableEventCard';
import WeekView from '../components/WeekView';
import CreateEventModal from '../components/CreateEventModal';

function CalendarPage({ acceptedEvents, updateAcceptedEvent, deleteAcceptedEvent, getCategoryStyles, formatDateTime, isMobile, setEventToEdit, acceptEvent, API_KEY }) {
  // Performance optimization for mobile navigation
  const hasLoadedInitialData = React.useRef(false);
  const [filteredEvents, setFilteredEvents] = useState(acceptedEvents);
  const [filters, setFilters] = useState({
    participants: [],
    categories: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('today'); // Default to today view
  const [todayEvents, setTodayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [multiDayEvents, setMultiDayEvents] = useState([]);
  const [showAllTomorrowEvents, setShowAllTomorrowEvents] = useState(false);
  // Refs for scroll position and animations
  const todayWrapperRef = useRef(null);
  const filtersRef = useRef(null);
  const scrollContainerRef = useRef(null);
  
  // State for create event modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState(null);
  const [createModalHour, setCreateModalHour] = useState(null);

  // Helper function to check if event passes participant filter
  const passesParticipantFilter = (event) => {
    // If no participant filters are active, all events pass
    if (filters.participants.length === 0) return true;

    // Define common participants for name lookup
    const commonParticipants = [
      { id: 'dad', name: 'Dad' },
      { id: 'mom', name: 'Mom' },
      { id: 'child', name: 'Child' },
      { id: 'driver', name: 'Driver' },
      { id: 'nanny', name: 'Nanny' }
    ];
    
    // Get event participant names as an array (handles both new and old format)
    let eventParticipants = [];
    
    // First check if we have the new participants array format
    if (event.participants && Array.isArray(event.participants)) {
      eventParticipants = event.participants.map(name => name.toLowerCase());
    }
    // Fall back to the old participant string format if needed
    else if (event.participant) {
      eventParticipants = event.participant.split(',').map(p => p.trim().toLowerCase());
    }
    // If no participants, event doesn't pass filter
    else {
      return false;
    }
    
    // Find matching participants
    return filters.participants.some(participantId => {
      const participant = commonParticipants.find(p => p.id === participantId);
      return participant && eventParticipants.includes(participant.name.toLowerCase());
    });
  };

  // Helper function to check if event passes category filter
  const passesCategoryFilter = (event) => {
    // If no category filters are active, all events pass
    if (filters.categories.length === 0) return true;
    
    // Check if event category is in the filter categories
    return filters.categories.includes(event.category);
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };
  
  // Day navigation functions have been removed

  // Simplified go to today function
  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison
    const currentDate = new Date(selectedDate);
    currentDate.setHours(0, 0, 0, 0);

    // Simple vibration feedback if supported
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(5);
    }

    // Show loading indicator
    setIsScrolling(true);

    // Set the date to today and switch to Today tab
    setSelectedDate(new Date());
    setActiveTab('today');

    // Hide loading after a delay to ensure rendering is complete
    setTimeout(() => {
      setIsScrolling(false);
      scrollToSelectedDay(true);
    }, 600);
  };
  
  // Swipe gestures for day navigation have been removed

  // Close filters when clicking outside
  useEffect(() => {
    if (!showFilters) return;

    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        // Check if the click is on the filter toggle button
        const isFilterButton = event.target.closest('.filter-toggle-button');
        if (!isFilterButton) {
          setShowFilters(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showFilters]);

  // Function to get events for a specific date (including recurring events)
  const getEventsForDate = (date, events) => {
    const dateEvents = [];

    events.forEach(event => {
      // First check if this is a direct date match (original event)
      const isDirectMatch = (
        event.day === date.getDate() &&
        event.month === date.getMonth() + 1 &&
        event.year === date.getFullYear()
      );

      // Check if event passes the filters
      const passesFilters = passesParticipantFilter(event) && passesCategoryFilter(event);

      if (isDirectMatch && passesFilters) {
        // Check if this event is already in the dateEvents array to prevent duplicates
        const isDuplicate = dateEvents.some(existingEvent => 
          existingEvent.id === event.id || 
          (existingEvent.googleId && event.googleId && existingEvent.googleId === event.googleId)
        );
        
        if (!isDuplicate) {
          dateEvents.push(event);
        } else {
          console.log(`Skipping duplicate direct match of event ${event.eventName}`);
        }
        return; // Skip the recurrence check since it's a direct match
      }

      // If it's not a direct match but is a recurring event, check recurrence rules
      if (event.isRecurring && passesFilters) {
        const eventStartDate = new Date(event.year, event.month - 1, event.day);
        const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // If target date is before event start date, it can't be a recurrence instance
        if (targetDate < eventStartDate) {
          return;
        }

        // Check if recurrence has ended by end date
        if (event.recurrenceEndDate) {
          const endDate = new Date(event.recurrenceEndDate);
          if (targetDate > endDate) {
            return;
          }
        }

        // Calculate days between event start and target date
        const diffTime = targetDate.getTime() - eventStartDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        let isRecurrenceMatch = false;
        const dayOfWeek = targetDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

        switch (event.recurrencePattern) {
          case 'daily':
            // Check if it's the correct day interval (e.g., every N days)
            isRecurrenceMatch = diffDays % event.recurrenceInterval === 0;
            break;

          case 'weekdays':
            // Check if it's a weekday (Monday-Friday) and the recurrence interval is met
            isRecurrenceMatch = (dayOfWeek > 0 && dayOfWeek < 6) &&
                               (Math.floor(diffDays / 7) % event.recurrenceInterval === 0);
            break;

          case 'weekly':
            // Check if it's the correct weekly interval and the same day of week
            isRecurrenceMatch = (diffDays % (7 * event.recurrenceInterval) === 0) &&
                               (targetDate.getDay() === eventStartDate.getDay());
            break;

          case 'monthly':
            // Check if it's the same day of the month and correct monthly interval
            const monthDiff = (targetDate.getFullYear() - eventStartDate.getFullYear()) * 12 +
                             targetDate.getMonth() - eventStartDate.getMonth();
            isRecurrenceMatch = (targetDate.getDate() === eventStartDate.getDate()) &&
                               (monthDiff % event.recurrenceInterval === 0);
            break;

          case 'yearly':
            // Check if it's the same month and day and correct yearly interval
            const yearDiff = targetDate.getFullYear() - eventStartDate.getFullYear();
            isRecurrenceMatch = (targetDate.getDate() === eventStartDate.getDate()) &&
                               (targetDate.getMonth() === eventStartDate.getMonth()) &&
                               (yearDiff % event.recurrenceInterval === 0);
            break;

          case 'custom':
            // Check if this day of week is selected and recurrence interval is met
            const isSelectedDay = (
              (dayOfWeek === 0 && event.selectedDays.sunday) ||
              (dayOfWeek === 1 && event.selectedDays.monday) ||
              (dayOfWeek === 2 && event.selectedDays.tuesday) ||
              (dayOfWeek === 3 && event.selectedDays.wednesday) ||
              (dayOfWeek === 4 && event.selectedDays.thursday) ||
              (dayOfWeek === 5 && event.selectedDays.friday) ||
              (dayOfWeek === 6 && event.selectedDays.saturday)
            );

            // Check if it's the correct week interval
            const weekDiff = Math.floor(diffDays / 7);
            isRecurrenceMatch = isSelectedDay && (weekDiff % event.recurrenceInterval === 0);
            break;
        }

        // Check recurrence count limit if specified
        if (isRecurrenceMatch && event.recurrenceCount > 0) {
          // Calculate the occurrence number for this instance
          let occurrenceNumber = 1; // Start count with the first event

          switch (event.recurrencePattern) {
            case 'daily':
              occurrenceNumber = 1 + Math.floor(diffDays / event.recurrenceInterval);
              break;
            case 'weekdays':
              // Count weekdays between start date and target date
              const weekdayCount = Array.from({ length: diffDays + 1 }, (_, i) => {
                const day = new Date(eventStartDate);
                day.setDate(day.getDate() + i);
                return day.getDay() > 0 && day.getDay() < 6 ? 1 : 0;
              }).reduce((sum, val) => sum + val, 0);
              occurrenceNumber = weekdayCount;
              break;
            case 'weekly':
              occurrenceNumber = 1 + Math.floor(diffDays / (7 * event.recurrenceInterval));
              break;
            case 'monthly':
              const monthDiff = (targetDate.getFullYear() - eventStartDate.getFullYear()) * 12 +
                               targetDate.getMonth() - eventStartDate.getMonth();
              occurrenceNumber = 1 + Math.floor(monthDiff / event.recurrenceInterval);
              break;
            case 'yearly':
              const yearDiff = targetDate.getFullYear() - eventStartDate.getFullYear();
              occurrenceNumber = 1 + Math.floor(yearDiff / event.recurrenceInterval);
              break;
            case 'custom':
              // Count matching selected days between start date and target date
              const weekDiff = Math.floor(diffDays / 7);
              occurrenceNumber = 1 + Math.floor(weekDiff / event.recurrenceInterval);
              break;
          }

          // Skip if we've exceeded the recurrence count
          if (occurrenceNumber > event.recurrenceCount) {
            return;
          }
        }

        // If we've determined this is a recurring instance, add it
        if (isRecurrenceMatch) {
          // Create a clone of the event with updated date
          const recurrenceInstance = {
            ...event,
            day: date.getDate(),
            month: date.getMonth() + 1,
            year: date.getFullYear(),
            originalEventId: event.id, // Reference to original event
            isRecurrenceInstance: true // Mark as a recurrence instance
          };

          // Check if this recurring instance is already in the dateEvents array
          const isDuplicate = dateEvents.some(existingEvent => {
            return (
              // Match by originalEventId (which will match to the source recurring event)
              (existingEvent.originalEventId === event.id || existingEvent.id === event.id) &&
              // And same date
              existingEvent.day === date.getDate() &&
              existingEvent.month === date.getMonth() + 1 &&
              existingEvent.year === date.getFullYear()
            );
          });
          
          // Only add if not a duplicate
          if (!isDuplicate) {
            dateEvents.push(recurrenceInstance);
          } else {
            console.log(`Skipping duplicate recurring instance of event ${event.eventName}`);
          }
        }
      }
    });

    return dateEvents;
  };

  // State to track how many days are loaded in each direction
  // Reduce initial load for mobile devices to improve performance
  const [pastDaysLoaded, setPastDaysLoaded] = useState(isMobile ? 60 : 365);
  const [futureDaysLoaded, setFutureDaysLoaded] = useState(isMobile ? 60 : 365);
  const [isLoadingMoreDays, setIsLoadingMoreDays] = useState(false);
  
  // Function to load events for multiple days (including past days)
  const loadMultiDayEvents = (startDate, filteredEvents) => {
    const multiDayEventsArray = [];

    // Show past days as well as future days - now using state variables
    const totalDaysToLoad = pastDaysLoaded + 1 + futureDaysLoaded; // Past + Today + Future

    // Set base date to include past days from the selected date
    let baseDate = new Date(startDate);
    baseDate.setDate(baseDate.getDate() - pastDaysLoaded);

    // Check if we have active filters
    const hasActiveFilters = filters.participants.length > 0 || filters.categories.length > 0;
    
    // Check which tab is active
    const isTodayTab = activeTab === 'today';

    for (let i = 0; i < totalDaysToLoad; i++) {
      const currentDate = new Date(baseDate);
      currentDate.setDate(currentDate.getDate() + i);

      const eventsForDay = getEventsForDate(currentDate, filteredEvents);

      // Check if tomorrow has school events
      let hasTomorrowSchoolEvents = false;
      const tomorrowDate = new Date(currentDate);
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      const tomorrowEvents = getEventsForDate(tomorrowDate, filteredEvents);
      hasTomorrowSchoolEvents = tomorrowEvents.some(event => 
        event.category && event.category.toLowerCase() === 'school'
      );
      
      // Debug logging
      if (hasTomorrowSchoolEvents && eventsForDay.length === 0) {
        console.log(`📚 Day ${currentDate.toDateString()} has no events but tomorrow has school events`);
      }

      // Today tab: Include days with events, selected day, today, or days with school events tomorrow
      // Month tab: Include all days regardless of event count
      if (!isTodayTab || eventsForDay.length > 0 || 
          isSameDay(currentDate, selectedDate) || 
          isSameDay(currentDate, new Date()) ||
          hasTomorrowSchoolEvents) {
        multiDayEventsArray.push({
          date: new Date(currentDate),
          events: eventsForDay
        });
      }
    }

    return multiDayEventsArray;
  };
  
  // Function to check if there are more events in a direction
  const hasMoreEventsInDirection = (direction, currentRange) => {
    const startDate = new Date(selectedDate);
    
    if (direction === 'past') {
      // Check if there are events before the current past range
      const checkStartDate = new Date(startDate);
      checkStartDate.setDate(checkStartDate.getDate() - currentRange - 30); // Check 30 days before current range
      const checkEndDate = new Date(startDate);
      checkEndDate.setDate(checkEndDate.getDate() - currentRange);
      
      // Look for events in the check range
      for (let i = 0; i < 30; i++) {
        const checkDate = new Date(checkStartDate);
        checkDate.setDate(checkDate.getDate() + i);
        const eventsForDay = getEventsForDate(checkDate, filteredEvents);
        if (eventsForDay.length > 0) {
          return true;
        }
      }
    } else {
      // Check if there are events after the current future range
      const checkStartDate = new Date(startDate);
      checkStartDate.setDate(checkStartDate.getDate() + currentRange);
      const checkEndDate = new Date(startDate);
      checkEndDate.setDate(checkEndDate.getDate() + currentRange + 30); // Check 30 days after current range
      
      // Look for events in the check range
      for (let i = 0; i < 30; i++) {
        const checkDate = new Date(checkStartDate);
        checkDate.setDate(checkDate.getDate() + i);
        const eventsForDay = getEventsForDate(checkDate, filteredEvents);
        if (eventsForDay.length > 0) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Function to load more days (past or future) - only if there are more events
  const loadMoreDays = (direction) => {
    const currentRange = direction === 'past' ? pastDaysLoaded : futureDaysLoaded;
    
    // Check if there are actually more events to show in this direction
    if (!hasMoreEventsInDirection(direction, currentRange)) {
      console.log(`No more events found in ${direction} direction, skipping load`);
      return;
    }
    
    setIsLoadingMoreDays(true);
    
    // Add more days in the specified direction
    // We increase the range more significantly when filters are active to ensure we find matching events
    // But use smaller ranges on mobile for better performance
    const hasActiveFilters = filters.participants.length > 0 || filters.categories.length > 0;
    const incrementAmount = isMobile ? 
      (hasActiveFilters ? 120 : 60) :  // Mobile: Load smaller chunks
      (hasActiveFilters ? 730 : 365);  // Desktop: Load larger chunks
    
    if (direction === 'past') {
      setPastDaysLoaded(prevDays => prevDays + incrementAmount);
    } else {
      setFutureDaysLoaded(prevDays => prevDays + incrementAmount);
    }
    
    // Short delay to allow state to update
    setTimeout(() => {
      setIsLoadingMoreDays(false);
    }, 500);
  };

  // Apply filters to events and get events for selected date
  useEffect(() => {
    // Mobile optimization: On first load, mark that we've loaded initial data
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
    }

    // Apply participant and category filters
    let filtered = acceptedEvents;
    if (filters.participants.length > 0 || filters.categories.length > 0) {
      filtered = acceptedEvents.filter(event =>
        passesParticipantFilter(event) && passesCategoryFilter(event)
      );
    }

    // Get events for the selected date
    const filteredDateEvents = getEventsForDate(selectedDate, filtered);

    // Load events for multiple days
    const multiDayEventsData = loadMultiDayEvents(selectedDate, filtered);

    setFilteredEvents(filtered);
    setTodayEvents(filteredDateEvents);
    setMultiDayEvents(multiDayEventsData);

    // Removed redundant scroll - handled in dedicated useEffect below
  }, [acceptedEvents, filters, selectedDate, activeTab, isMobile]);

  // Separate date display in the header area that updates with selected date
  // without affecting tab behavior or displayed content
  const [dateDisplayText, setDateDisplayText] = useState('');
  // Track the last visible day while scrolling
  const [visibleDayDate, setVisibleDayDate] = useState(null);

  // Update date display text when selectedDate or visibleDayDate changes
  useEffect(() => {
    // Use visibleDayDate if available (from scrolling), otherwise use selectedDate
    const dateToDisplay = visibleDayDate || selectedDate;

    const formattedDate = dateToDisplay.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
    const isToday = isSameDay(dateToDisplay, new Date());

    // For current day show (Today) instead of day of week
    if (isToday) {
      setDateDisplayText(`${formattedDate} (Today)`);
    } else {
      const weekday = dateToDisplay.toLocaleDateString(undefined, { weekday: 'short' }).slice(0,3);
      setDateDisplayText(`${formattedDate} (${weekday})`);
    }
  }, [selectedDate, visibleDayDate]);

  // State to track loading state when scrolling to selected day
  const [isScrolling, setIsScrolling] = useState(false);

  // Function to scroll to the selected day with a loading indicator
  // Adds an offset to position the day slightly lower in the viewport
  const scrollToSelectedDay = (withHighlight = false) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const selectedDayElement = document.querySelector('.day-events-group.selected-day');
    if (selectedDayElement) {
      // Show loading indicator
      setIsScrolling(true);

      // Get the position within the scroll container
      const dayPosition = selectedDayElement.offsetTop;
      
      // Add an offset to position the day slightly lower (negative offset means showing part of day above)
      const verticalOffset = 40; // Negative value to show part of current day cut off at top
      
      // Double-check that ref still exists before scrolling (it could be unmounted during this function)
      if (scrollContainerRef.current) {
        // Scroll to position with offset
        scrollContainerRef.current.scrollTop = dayPosition + verticalOffset;
      }

      // Hide loading indicator after the scroll is complete
      setTimeout(() => {
        setIsScrolling(false);
      }, 400);
    }
  };

  // Effect to handle scroll events, update tab text based on visible day, and detect scroll position for infinite scrolling
  useEffect(() => {
    if (!scrollContainerRef.current || activeTab !== 'today') return;

    const handleScroll = (e) => {
      // Ensure scrollContainerRef.current exists before accessing properties
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;
      
      // Find which day separator is currently most visible
      const daySeparators = document.querySelectorAll('.day-separator');
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const containerHeight = scrollContainer.clientHeight;

      // Check if we're near the top or bottom for infinite scrolling
      // Only trigger infinite scroll if there's sufficient content to actually scroll
      const minContentHeight = containerHeight * 1.5; // Need at least 1.5x container height to enable infinite scroll
      if (!isLoadingMoreDays && scrollHeight > minContentHeight) {
        // If we're near the top (about 15% from the top), load more past days
        // Using a larger threshold when filtering is active since days might be sparse
        const hasActiveFilters = filters.participants.length > 0 || filters.categories.length > 0;
        const topThreshold = hasActiveFilters ? 0.2 : 0.1; // 20% when filtering, 10% normally
        const bottomThreshold = hasActiveFilters ? 0.8 : 0.9; // 80% when filtering, 90% normally
        
        if (scrollTop < containerHeight * topThreshold) {
          // Remember the current scroll position and scroll height
          const currentScrollHeight = scrollHeight;
          const currentScrollPosition = scrollTop;
          
          // Load more past days
          loadMoreDays('past');
          
          // After the DOM updates, adjust scroll position to maintain relative view
          // Temporarily disabled to prevent jumping
          /*
          setTimeout(() => {
            const scrollContainer = scrollContainerRef.current;
            if (!scrollContainer) return;
            const newScrollHeight = scrollContainer.scrollHeight;
            const scrollDifference = newScrollHeight - currentScrollHeight;
            scrollContainer.scrollTop = currentScrollPosition + scrollDifference;
          }, 100);
          */
        }
        
        // If we're near the bottom, load more future days
        if (scrollTop + containerHeight > scrollHeight * bottomThreshold) {
          loadMoreDays('future');
        }
      }
      
      // Track which day is most visible
      let mostVisibleDay = null;
      let largestVisibleArea = -1;

      daySeparators.forEach(separator => {
        const dayGroup = separator.closest('.day-events-group');
        if (!dayGroup) return;

        // Get position data for visibility calculation
        const rect = dayGroup.getBoundingClientRect();
        const containerRect = scrollContainerRef.current.getBoundingClientRect();

        // Calculate how much of this day is visible in the viewport
        const visibleTop = Math.max(rect.top, containerRect.top);
        const visibleBottom = Math.min(rect.bottom, containerRect.top + containerHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > largestVisibleArea) {
          largestVisibleArea = visibleHeight;

          // Try to extract the date from this element
          try {
            // Find the corresponding day data by index
            const index = Array.from(daySeparators).indexOf(separator);
            if (index >= 0 && index < multiDayEvents.length) {
              mostVisibleDay = multiDayEvents[index].date;
            }
          } catch (e) {
            console.log('Error finding date:', e);
          }
        }
      });

      // Update the visible day if we found one and it's different from current
      if (mostVisibleDay &&
          (!visibleDayDate || !isSameDay(mostVisibleDay, visibleDayDate))) {
        setVisibleDayDate(mostVisibleDay);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    scrollContainer.addEventListener('scroll', handleScroll);

    // Initialize with a scroll event to set initial value
    handleScroll();

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeTab, multiDayEvents, visibleDayDate, isLoadingMoreDays]);

  // Initial load useEffect - will only run once when the component first mounts
  useEffect(() => {
    // Check if there's a date parameter in the URL
    const searchParams = new URLSearchParams(window.location.search);
    const dateParam = searchParams.get('date');
    
    let initialDate;
    
    if (dateParam && !isNaN(Date.parse(dateParam))) {
      // Use the date from URL parameter
      initialDate = new Date(dateParam);
      
      // Also set the active tab to 'today' to show the day view
      setActiveTab('today');
    } else {
      // Default to today's date if no valid date parameter
      initialDate = new Date();
    }
    
    setSelectedDate(initialDate);
    
    // Track if component is mounted for async operations
    let isMounted = true;

    // Set up automatic Google Calendar sync
    const setupGoogleCalendarSync = () => {
      try {
        // Check if user is authenticated with Google Calendar
        const isAuthenticated = localStorage.getItem('googleAccessToken');
        if (isAuthenticated) {
          console.log('Calendar page mounted - user authenticated with Google Calendar');
          
          // Perform initial sync
          syncWithGoogleCalendar();
          
          // Set up interval for periodic syncs every 60 seconds
          const syncInterval = setInterval(() => {
            console.log('Periodic Google Calendar sync running...');
            syncWithGoogleCalendar();
          }, 60000); // 60000 ms = 1 minute
          
          // Return cleanup function to clear interval when component unmounts
          return () => {
            console.log('Clearing Google Calendar sync interval');
            clearInterval(syncInterval);
          };
        }
      } catch (error) {
        console.error('Error setting up Google Calendar sync:', error);
      }
    };
    
    // Function to sync with Google Calendar
    const syncWithGoogleCalendar = async () => {
      try {
        // Check if user is authenticated with Google Calendar
        const isAuthenticated = localStorage.getItem('googleAccessToken');
        if (!isAuthenticated) {
          console.log('Not authenticated with Google Calendar');
          return;
        }
        
        // Import the sync function
        const { syncGoogleCalendar } = await import('../services/googleCalendarSync');
        
        console.log('Syncing with Google Calendar...');
        
        // Pass the acceptEvent function to handle the imported events
        const result = await syncGoogleCalendar((googleEvents) => {
          // Check if component is still mounted before updating state
          if (!isMounted) {
            console.log('Component unmounted, skipping event processing');
            return;
          }
          
          if (googleEvents && googleEvents.length > 0) {
            console.log(`Processing ${googleEvents.length} events from Google Calendar`);
            // Handle each imported event by passing to App.js acceptEvent function
            googleEvents.forEach(async (event) => {
              try {
                console.log('Processing Google Calendar event:', event.eventName);
                
                // Import Firebase functions to directly add to collection
                const { getFirestore, collection, query, where, getDocs, addDoc } = await import('firebase/firestore');
                const { db } = await import('../firebase');
                
                // Prepare the event data
                const eventData = {
                  ...event,
                  createdAt: new Date().toISOString(),
                  source: 'google'
                };
                
                // Comprehensive duplicate checking BEFORE adding to Firestore
                // First check if this Google Calendar event ID already exists
                if (event.googleId) {
                  const googleIdQuery = query(
                    collection(db, "acceptedEvents"),
                    where("googleId", "==", event.googleId)
                  );
                  
                  const googleIdResults = await getDocs(googleIdQuery);
                  
                  if (!googleIdResults.empty) {
                    console.log(`Duplicate found via googleId for event "${event.eventName}"`);
                    return;
                  }
                }
                
                // Then check by date + title
                const dateNameQuery = query(
                  collection(db, "acceptedEvents"),
                  where("eventName", "==", event.eventName), 
                  where("day", "==", event.day),
                  where("month", "==", event.month),
                  where("year", "==", event.year)
                );
                
                const dateNameResults = await getDocs(dateNameQuery);
                
                if (!dateNameResults.empty) {
                  console.log(`Duplicate found via date+name for event "${event.eventName}" on ${event.month}/${event.day}/${event.year}`);
                  return;
                }
                
                // Add directly to the acceptedEvents collection after passing duplicate checks
                const acceptedEventsRef = collection(db, "acceptedEvents");
                const docRef = await addDoc(acceptedEventsRef, eventData);
                console.log('Added Google Calendar event directly to Firebase with ID:', docRef.id);
                
                // Update local state
                const newEvent = {
                  ...eventData,
                  id: docRef.id,
                  firestoreId: docRef.id
                };
                
                // Local state duplicate check only - Firestore checks were done before adding
                
                // Also check local state for duplicates
                const existsInLocalState = acceptedEvents.some(e => 
                  // Check for identical key properties
                  (e.eventName === newEvent.eventName && 
                   e.day === newEvent.day && 
                   e.month === newEvent.month && 
                   e.year === newEvent.year) ||
                  // Check for Google ID match if available
                  (e.googleId && newEvent.googleId && e.googleId === newEvent.googleId)
                );
                
                if (existsInLocalState) {
                  console.log('Event already exists in acceptedEvents, skipping:', newEvent.eventName);
                } else {
                  // Since we've already added to Firebase, we can let the App component
                  // know about this event so it can update its state properly
                  console.log('Adding new Google Calendar event to state:', newEvent.eventName);
                  
                  updateAcceptedEvent({
                    ...newEvent,
                    // Set a flag to indicate this is a direct add that already has a Firestore ID
                    isDirectAdd: true,
                    // Add source info for better filtering
                    source: 'google',
                    googleImportTime: new Date().toISOString()
                  });
                }
              } catch (error) {
                console.error('Error adding Google Calendar event:', error);
              }
            });
          }
        });
        
        if (isMounted) {
          console.log('Google Calendar sync result:', result);
        }
      } catch (error) {
        console.error('Error syncing with Google Calendar:', error);
      }
    };
    
    // Set up the sync process
    const cleanup = setupGoogleCalendarSync();
    
    // Add cleanup function to useEffect cleanup
    return () => {
      isMounted = false;
      if (cleanup) cleanup();
    };

    // We'll handle auto-scrolling in another effect
  }, []); // Empty dependency array makes this run only once at component mount

  // Scroll handling useEffect - runs when selected date or tab changes
  useEffect(() => {
    // Only proceed if we're in day view and have events loaded
    if (activeTab === 'today' && multiDayEvents.length > 0) {
      // Use a single delayed scroll to avoid jumping
      const scrollTimeout = setTimeout(() => {
        scrollToSelectedDay(false); // No highlight on automatic scroll
      }, 200);

      // Cleanup timeout on unmount or dependency change
      return () => clearTimeout(scrollTimeout);
    }
  }, [multiDayEvents.length, activeTab, selectedDate, isMobile]); // Added isMobile dependency

  // Removed auto-scrolling offset effect

  // Render the day view events section with multi-day scrollable view
  const renderDayEventsSection = () => (
    <div
      className="today-events-wrapper"
      ref={todayWrapperRef}
      style={{
        marginTop: isMobile ? '0' : '0', // Removed negative margin
        paddingTop: '0',
        height: '100%', // Full height to enable scrolling
        maxHeight: isMobile ? 'calc(100vh)' : 'calc(100vh - 210px)', // Match parent container sizes
        overflow: 'hidden', // Hide overflow during transitions
        position: 'relative', // For animation positioning
        perspective: '1000px', // For 3D effect during transitions
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Day navigation buttons have been removed */}
      
      {/* Container for days without sliding transitions */}
      <div className="day-events-container" style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>

        {/* Current day events with enhanced animation */}
        {/* Loading overlay that shows during scrolling */}
        {isScrolling && (
          <div
            className="loading-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#f7f7f8',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 1,
              transition: 'opacity 0.2s ease'
            }}
          >
            <div
              className="loading-spinner"
              style={{
                width: '36px',
                height: '36px',
                border: '3px solid rgba(0,0,0,0.1)',
                borderTop: '3px solid #1c83c9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="day-events-scroll-container"
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100%',
            flex: 1,
            visibility: isScrolling ? 'hidden' : 'visible', // Hide content during scrolling
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            maxWidth: !isMobile ? '700px' : 'none',
            margin: !isMobile ? '0 auto' : '0'
          }}
        >
          <style>
            {`
              /* Minimal styles without transitions */
              .day-events-group {
                border-radius: 12px;
              }

              /* Touch feedback for event cards */
              .expandable-event-card {
                transition: transform 0.25s ease,
                            box-shadow 0.25s ease,
                            background-color 0.25s ease;
              }

              .expandable-event-card:active {
                transform: scale(0.98);
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
              }

              /* Loading spinner animation */
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              /* Infinite scroll loading indicators */
              .infinite-scroll-loader {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 20px 0;
                height: 40px;
              }
              
              .loading-spinner {
                width: 24px;
                height: 24px;
                border: 2px solid rgba(0,0,0,0.1);
                border-top: 2px solid #1c83c9;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              
              /* Event highlight animation */
              @keyframes eventHighlight {
                0% { 
                  transform: scale(1);
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
                }
                50% { 
                  transform: scale(1.02);
                  box-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);
                }
                100% { 
                  transform: scale(1);
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
                }
              }
              
              .event-highlight {
                animation: eventHighlight 0.6s ease-in-out 2;
                border: 2px solid #4361ee !important;
              }
            `}
          </style>
          
        {/* Top loading indicator for past days */}
        <div className="infinite-scroll-loader">
          {isLoadingMoreDays && 
            <div className="loading-spinner"></div>
          }
        </div>
          
        {multiDayEvents.length === 0 && (filters.participants.length > 0 || filters.categories.length > 0) ? (
          <div className="no-matching-days" style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '1rem',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '15px'
          }}>
            <p>No events match your current filters</p>
            <button 
              onClick={() => setFilters({participants: [], categories: []})}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1c83c9',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              Clear All Filters
            </button>
          </div>
        ) : 
          <div style={{
            display: 'block',
            padding: !isMobile ? '0 20px' : '0'
          }}>
          {multiDayEvents.map((dayData, index) => {
            // Check if tomorrow has school events
            let hasTomorrowSchoolEvents = false;
            if (index < multiDayEvents.length - 1) {
              const tomorrowDate = new Date(dayData.date);
              tomorrowDate.setDate(tomorrowDate.getDate() + 1);
              const tomorrowEvents = getEventsForDate(tomorrowDate, filteredEvents);
              hasTomorrowSchoolEvents = tomorrowEvents.some(event => 
                event.category && event.category.toLowerCase() === 'school'
              );
            }
            
            // Only render days with events, selected day, today, or days with school tomorrow
            return (dayData.events.length > 0 || 
            isSameDay(dayData.date, selectedDate) || 
            isSameDay(dayData.date, new Date()) ||
            hasTomorrowSchoolEvents) && (
              <div
                className={`day-events-group ${isSameDay(dayData.date, selectedDate) ? 'selected-day' : ''}`}
                key={index}
                style={{
                  marginBottom: isMobile && index < multiDayEvents.length - 1 ? '15px' : '0', // Only show margin on mobile between items
                  scrollMarginTop: '0', // Zero margin for exact positioning at top
                  borderRadius: '8px',
                  padding: '6px 0'
                }}
              >
                {/* Day separator - shown for all days including today */}
                <div className="day-separator" style={{
                  padding: isMobile ? '10px 28px 4px 28px' : '10px 16px 4px 16px',
                  fontWeight: isSameDay(dayData.date, selectedDate) ? '600' : 'normal',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: '#666',
                  marginBottom: '8px',
                  marginTop: '0', // Consistent margin for all days
            
                  textAlign: 'left',
                  position: 'relative',
                  zIndex: 2,
                  backgroundColor: 'transparent',
                  opacity: 1
                }}>
                  <span>
                    {dayData.date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
                    {isSameDay(dayData.date, new Date()) ?
                      ' (Today)' :
                      ` (${dayData.date.toLocaleDateString(undefined, { weekday: 'short' }).slice(0,3)})`
                    }
                  </span>
                </div>
                
                {/* Event list */}
                {dayData.events.length > 0 ? (
                  <div className="events-list" style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    minHeight: `${Math.min(dayData.events.length * 70, 400)}px`, // Reduced height based on number of events, max 400px
                    height: 'auto',
                    paddingBottom: isMobile ? (index < multiDayEvents.length - 1 &&
                                   !isSameDay(dayData.date, selectedDate) &&
                                   isSameDay(multiDayEvents[index + 1].date, selectedDate)
                                   ? '20px' : '0') : '0' // No padding on desktop since days are side by side
                  }}>
                    {[...dayData.events]
                      .sort((a, b) => {
                        // First, put all-day events at the top
                        if (a.isAllDay && !b.isAllDay) return -1;
                        if (!a.isAllDay && b.isAllDay) return 1;
                        
                        // Then sort by time for events with time
                        if (!a.isAllDay && !b.isAllDay) {
                          const aHour = a.hour || 0;
                          const aMinute = a.minute || 0;
                          const bHour = b.hour || 0;
                          const bMinute = b.minute || 0;
                          
                          // Compare hours first, then minutes
                          if (aHour !== bHour) return aHour - bHour;
                          return aMinute - bMinute;
                        }
                        
                        // For all-day events, sort alphabetically by name
                        return a.eventName.localeCompare(b.eventName);
                      })
                      .map((event) => (
                        <ExpandableEventCard
                          key={event.id}
                          event={event}
                          onEdit={() => {
                            setEventToEdit(event);
                          }}
                          onDelete={deleteAcceptedEvent}
                          getCategoryStyles={getCategoryStyles}
                          formatDateTime={(event) => formatDateTime(event, true)}
                          isMobile={isMobile}
                          displayDate={dayData.date}
                        />
                      ))}
                  </div>
                ) : (
                  <div className="no-events" style={{
                    padding: '10px',
                    textAlign: 'center',
                    color: '#888',
                    fontSize: '0.9rem',
                    minHeight: '60px', // Reduced minimum height for more compact spacing
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <p>
                      {filters.participants.length > 0 || filters.categories.length > 0 
                        ? 'No matching events for your filters' 
                        : 'No events scheduled'}
                    </p>
                  </div>
                )}
                
                {/* School tomorrow section - show if not the last day */}
                {index < multiDayEvents.length - 1 && (() => {
                  let upcomingEvents = [];
                  let sectionTitle = "School tomorrow";
                  
                  // Check if current day is Sunday (0 in JavaScript)
                  const isSunday = dayData.date.getDay() === 0;
                  
                  if (isSunday) {
                    // For Sunday, show all school events for the upcoming week (Monday to next Sunday)
                    sectionTitle = "School this week";
                    
                    // Start from Monday (next day)
                    const startDate = new Date(dayData.date);
                    startDate.setDate(startDate.getDate() + 1); // Monday
                    
                    // End on next Sunday (7 days from Monday)
                    const endDate = new Date(startDate);
                    endDate.setDate(endDate.getDate() + 6); // Next Sunday
                    
                    // Collect all school events for the week
                    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                      const currentDate = new Date(d);
                      const dayEvents = getEventsForDate(currentDate, filteredEvents);
                      const daySchoolEvents = dayEvents.filter(event => 
                        event.category && (event.category.toLowerCase() === 'school' || event.category.toLowerCase() === 'class')
                      );
                      
                      daySchoolEvents.forEach(event => {
                        upcomingEvents.push({
                          ...event,
                          displayDate: new Date(currentDate)
                        });
                      });
                    }
                    
                    // Sort events by date and time
                    upcomingEvents.sort((a, b) => {
                      // First sort by date
                      const dateCompare = a.displayDate - b.displayDate;
                      if (dateCompare !== 0) return dateCompare;
                      
                      // Then by time
                      if (a.isAllDay && !b.isAllDay) return -1;
                      if (!a.isAllDay && b.isAllDay) return 1;
                      
                      if (!a.isAllDay && !b.isAllDay) {
                        const aTime = (a.hour || 0) * 60 + (a.minute || 0);
                        const bTime = (b.hour || 0) * 60 + (b.minute || 0);
                        return aTime - bTime;
                      }
                      
                      return 0;
                    });
                  } else {
                    // For other days, get school events for tomorrow only
                    const tomorrowDate = new Date(dayData.date);
                    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
                    const tomorrowEvents = getEventsForDate(tomorrowDate, filteredEvents);
                    // Filter for school and class events only
                    const schoolEvents = tomorrowEvents.filter(event => 
                      event.category && (event.category.toLowerCase() === 'school' || event.category.toLowerCase() === 'class')
                    );
                    upcomingEvents = schoolEvents.map(event => ({
                      ...event,
                      displayDate: tomorrowDate
                    }));
                  }
                  
                  if (upcomingEvents.length > 0) {
                    return (
                      <div className={`events-list ${isMobile ? 'school-tomorrow-mobile' : 'school-tomorrow-desktop'}`} style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        marginTop: isMobile ? '10px' : '0px'
                      }}>
                        <div
                          style={{
                            background: isSunday ? 
                              'linear-gradient(135deg, #f07818 0%, #f59140 100%)' : 
                              'linear-gradient(135deg, #5550B7 0%, #7B68C4 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            padding: isMobile ? '16px 16px 8px 16px' : '18px 16px 10px 16px',
                            width: isMobile ? 'calc(100% - 20px)' : '100%',
                            boxSizing: 'border-box',
                            margin: isMobile ? '10px auto 4px auto' : '0px auto 5px auto',
                            position: 'relative',
                            left: '0',
                            right: '0',
                            cursor: 'default',
                            transition: 'opacity 0.2s ease'
                          }}>
                        <div style={{
                          fontSize: isMobile ? '0.9rem' : '1rem',
                          color: 'white',
                          marginBottom: '6px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <img 
                            src="/graduation-cap.svg" 
                            alt="School" 
                            width="20" 
                            height="20"
                            style={{ filter: 'brightness(0) invert(1)' }}
                          />
                          {sectionTitle}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3px'
                        }}>
                          {upcomingEvents.map((event, idx) => (
                            <div
                              key={event.id || idx}
                              style={{
                                fontSize: isMobile ? '0.8rem' : '0.85rem',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '2px 0'
                              }}
                            >
                              <span style={{
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                flexShrink: 0
                              }} />
                              <span 
                                style={{
                                  flex: 1,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Navigate to the event's date and scroll to the event
                                  const eventDate = event.displayDate || new Date(dayData.date);
                                  eventDate.setDate(eventDate.getDate() + (event.displayDate ? 0 : 1));
                                  
                                  setIsScrolling(true);
                                  setSelectedDate(eventDate);
                                  setActiveTab('today');
                                  
                                  // Wait for tab switch and rendering, then scroll to specific event
                                  setTimeout(() => {
                                    setIsScrolling(false);
                                    
                                    // First ensure the correct day is visible
                                    const selectedDayElement = document.querySelector('.day-events-group.selected-day');
                                    const scrollContainer = scrollContainerRef.current;
                                    
                                    if (selectedDayElement && scrollContainer) {
                                      // Scroll to the selected day first
                                      const dayPosition = selectedDayElement.offsetTop;
                                      scrollContainer.scrollTop = dayPosition - 40;
                                      
                                      // Then find and scroll to the specific event
                                      setTimeout(() => {
                                        const eventElement = document.querySelector(`[data-event-id="${event.id}"]`);
                                        if (eventElement) {
                                          // Use scrollIntoView for more reliable scrolling
                                          eventElement.scrollIntoView({ 
                                            behavior: 'smooth', 
                                            block: 'center',
                                            inline: 'nearest' 
                                          });
                                          
                                          // Add highlight effect to the event
                                          eventElement.classList.add('event-highlight');
                                          
                                          // Click the event to expand it
                                          setTimeout(() => {
                                            eventElement.click();
                                          }, 500); // Wait for scroll to complete
                                          
                                          // Remove highlight after animation
                                          setTimeout(() => {
                                            eventElement.classList.remove('event-highlight');
                                          }, 2000);
                                        } else {
                                          console.warn(`Event element with id ${event.id} not found`);
                                        }
                                      }, 300); // Small delay to ensure day scroll completes
                                    }
                                  }, 800); // Slightly longer delay to ensure rendering is complete
                                }}
                              >
                                {isSunday && event.displayDate ? 
                                  `${event.eventName} (${event.displayDate.toLocaleDateString(undefined, { weekday: 'short' }).slice(0,3)})` : 
                                  event.eventName}
                              </span>
                              <span style={{
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                color: 'white',
                                flexShrink: 0
                              }}>
                                {event.isAllDay ? 'All day' : (() => {
                                  const hour = event.hour || 0;
                                  const minute = (event.minute || 0).toString().padStart(2, '0');
                                  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                  const ampm = hour < 12 ? 'AM' : 'PM';
                                  return `${displayHour}:${minute} ${ampm}`;
                                })()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )
          })}
          </div>
        }
        
        {/* Bottom loading indicator for future days */}
        <div className="infinite-scroll-loader">
          {isLoadingMoreDays && 
            <div className="loading-spinner"></div>
          }
        </div>
      </div>
      </div>
    </div>
  );
{/* Main page container - full height with no scroll */}
  return (
    
    <div className="page-container" style={{
      width: isMobile ? '100vw' : '100%',
      padding: 0,
      margin: 0,
      maxWidth: '100vw',
      overflow: 'hidden', // Prevents page scrolling
      height: isMobile ? '90vh' : 'calc(100vh - 70px)', // Full viewport height on mobile
      position: 'fixed', // Fixed positioning on mobile to prevent scrolling
      top: isMobile ? 0 : 'auto',
      left: isMobile ? 0 : 'auto',
      right: isMobile ? 0 : 'auto',
      bottom: isMobile ? 0 : 'auto'
    }}>
      {/* Fixed header section - stays at top of viewport */}
      <div className="sticky-header" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'var(--bg-color)',
        width: '100%',
        maxWidth: !isMobile ? (activeTab === 'today' ? '700px' : 'none') : 'none',
        margin: !isMobile ? '0 auto' : '0'
      }}>
        {/* Header container with title and controls */}
        <div className="calendar-page-header" style={{
          borderBottom: 'none',
          marginBottom: '12px', // Space between header and tabs
          paddingBottom: '8px',
        }}>
          {/* Title section - left side of header */}
          <div className="calendar-header-title" style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '10px'
          }}>
            {/* Page title "Calendar" */}
            <h2 style={{
              marginRight: isMobile ? '0' : '12px',
              marginBottom: '4px',
              marginTop: '8px'
            }}>Calendar</h2>
          </div>
          {/* Control buttons - right side of header */}
          <div className="calendar-header-buttons" style={{
            display: 'flex',
            alignItems: 'center',
            paddingRight: '12px'
          }}>
            {/* "Today" button - jumps to current date */}
            <button
              className="day-square-button"
              onClick={goToToday}
              title="Go to Today"
              style={{
                width: 'auto',
                padding: '0 12px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: !isSameDay(selectedDate, new Date()) ? '#1c83c9' : 'transparent',
                color: !isSameDay(selectedDate, new Date()) ? 'white' : '#1c83c9',
                fontWeight: '600',
                fontSize: '16px',
                borderRadius: '8px',
                border: '2px solid #1c83c9',
                marginRight: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Today
            </button>


            {/* Add new event button - navigates to upload page */}
            <button
              className="add-event-button"
              onClick={() => window.location.href = '/upload'}
              title="Add New Event"
            >
              {/* Plus icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>


        {/* Tab navigation section - switches between Today/Month views */}
        <div className="calendar-tabs-container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0',
          marginTop: '0',
          paddingBottom: '0',
        }}>
          {/* Tab buttons container */}
          <div className="calendar-tabs" style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* CSS for tab animations */}
            <style>
              {`
                .calendar-tab {
                  position: relative;
                  transition: all 0.8s ease-in-out;
                  overflow: hidden;
                }

                .calendar-tab.active {
                  color: #000000;
                  transform: scale(1.05);
                  transition: all 0.5s ease-in-out;
                  box-shadow: none;
                  background: transparent;
                }

                .calendar-tab:active {
                  transform: scale(0.98);
                }
              `}
            </style>
            {/* Tab button group */}
            <div style={{ display: 'flex' }} className="calendar-tab-buttons">
              {/* Today tab button - shows events for selected date */}
              <button
                className={`calendar-tab today ${activeTab === 'today' ? 'active' : ''}`}
                onClick={() => setActiveTab('today')}
                style={{
                  padding: '6px 12px',
                  fontWeight: activeTab === 'today' ? '700' : '600',
                  fontSize: isMobile ? '0.95rem' : '1.1rem',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: activeTab === 'today' ? '#000000' : '#444',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {dateDisplayText}
              </button>
            </div>

            {/* Right side of tab controls - Week and Month buttons and filter toggle */}
            <div className="tab-controls-right" style={{ display: 'flex', alignItems: 'center' }}>
              {/* Week tab button - shows only week view */}
              <button
                className={`calendar-tab week ${activeTab === 'week' ? 'active' : ''}`}
                onClick={() => setActiveTab('week')}
                style={{
                  padding: '6px 12px',
                  marginRight: '5px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '0.95rem' : '1.1rem',
                  fontWeight: activeTab === 'week' ? '700' : '600',
                  color: activeTab === 'week' ? '#000000' : '#444',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Week
              </button>
              {/* Month tab button - switches to monthly calendar view */}
              <button
                className={`calendar-tab month ${activeTab === 'month' ? 'active' : ''}`}
                onClick={() => setActiveTab('month')}
                style={{
                  padding: '6px 12px',
                  marginRight: isMobile ? '5px' : '10px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '0.95rem' : '1.1rem',
                  fontWeight: activeTab === 'month' ? '700' : '600',
                  color: activeTab === 'month' ? '#000000' : '#444',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Month
              </button>

              {/* Filter toggle button for mobile - shows/hides event filters */}
              {isMobile && (
                <button
                  className="filter-toggle-button"
                  onClick={() => setShowFilters(!showFilters)}
                  title={showFilters ? "Hide Filters" : "Show Filters"}
                  style={{
                    marginRight: '12px',
                    padding: '6px 12px',
                    backgroundColor: showFilters ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    color: showFilters ? '#000000' : '#444444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    borderBottom: 'none',
                    height: '36px', // Match the height of the Month tab
                    transition: 'all 0.2s ease-in-out',
                    transform: showFilters ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: 'none'
                  }}
                >
                  {/* Filter icon - rotates when filters are shown */}
                  <img
                    src="/sort.png"
                    alt="Filter"
                    width="22"
                    height="22"
                    style={{
                      marginRight: '3px',
                      transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease-in-out'
                    }}
                  />
                  {/* Filter count badge - shows number of active filters */}
                  {filters.participants.length > 0 || filters.categories.length > 0 ?
                    <span className="filter-badge" style={{
                      transition: 'transform 0.2s ease-in-out',
                      transform: showFilters ? 'scale(1.1)' : 'scale(1)'
                    }}>{filters.participants.length + filters.categories.length}</span> : null}
                </button>
              )}
            </div>
          </div>

          {/* Filter toggle button for desktop - shows/hides event filters */}
          {!isMobile && (
            <button
              className="filter-toggle-button"
              onClick={() => setShowFilters(!showFilters)}
              title={showFilters ? "Hide Filters" : "Show Filters"}
              style={{
                marginLeft: '10px',
                marginRight: '12px',
                padding: '6px 12px',
                backgroundColor: showFilters ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '500',
                color: showFilters ? '#000000' : '#444444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                borderBottom: 'none',
                height: '36px',
                transition: 'all 0.2s ease-in-out',
                transform: showFilters ? 'scale(1.05)' : 'scale(1)',
                boxShadow: 'none'
              }}
            >
              {/* Filter icon - rotates when filters are shown */}
              <img
                src="/sort.png"
                alt="Filter"
                width="22"
                height="22"
                style={{
                  marginRight: '3px',
                  transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out'
                }}
              />
              {/* Filter count badge - shows number of active filters */}
              {filters.participants.length > 0 || filters.categories.length > 0 ?
                <span className="filter-badge" style={{
                  transition: 'transform 0.2s ease-in-out',
                  transform: showFilters ? 'scale(1.1)' : 'scale(1)'
                }}>{filters.participants.length + filters.categories.length}</span> : null}
            </button>
          )}
        </div>
      </div>
      
      {/* Filters dropdown container - slides down when filter button is clicked */}
      <div
        className="filters-container"
        ref={filtersRef}
        style={{
          borderTop: 'none',
          borderBottom: 'none',
          position: 'fixed',
          top: isMobile ? '126px' : '136px', // Adjusted to account for added space
          left: 0,
          right: 0,
          zIndex: 999,
          backgroundColor: '#f5f5f5',
          maxHeight: showFilters ? '300px' : '0',
          overflow: 'hidden',
          opacity: showFilters ? 1 : 0,
          transform: showFilters ? 'translateY(0)' : 'translateY(-20px)',
          transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          boxShadow: showFilters ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'
        }}
      >
        <EventFilters
          filters={filters}
          onFilterChange={setFilters}
          isMobile={isMobile}
        />
      </div>
      
      {/* Main content area - contains tab content */}
      {/* Add padding to create space for the fixed header */}
      <div className="calendar-content" style={{
        marginTop: isMobile ? '126px' : '140px', // Increased top margin for desktop to account for fixed header
        width: '100%',
        height: isMobile ? 'calc(100vh - 126px - 56px)' : 'calc(100vh - 140px - 70px)', // Account for header + nav
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent double scrolling
        transition: 'padding-top 0.3s ease-in-out', // Smooth transition for padding changes
        paddingTop: showFilters ? (isMobile ? '70px' : '70px') : '0', // Add padding when filters are shown
        position: 'relative'
      }}>
        {/* Add a wrapper div for tab transition animations */}
        <style>
          {`
            .tab-content {
              transition: opacity 0.8s ease-in-out, transform 0.8s ease-in-out;
              width: 100%;
              height: 100%;
            }

            .tab-content.visible {
              opacity: 1;
              transform: translateY(0);
            }

            .tab-content.hidden {
              opacity: 0;
              transform: translateY(25px);
              position: absolute;
              pointer-events: none;
            }

            .tab-container {
              position: relative;
              width: 100%;
              height: 100%;
              overflow: hidden;
              display: flex;
              flex-direction: column;
            }
          `}
        </style>

        {/* Tab content container - holds both tab views */}
        <div className="tab-container">
          {/* Month Tab Content - shows calendar grid */}
          <div
            className={`tab-content month-tab ${activeTab === 'month' ? 'visible' : 'hidden'}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            {/* Calendar section wrapper - contains the calendar component */}
            <div className="calendar-section-container" style={{
              width: '100%',
              position: 'relative',
              marginBottom: '20px',
              marginTop: '12px' /* Add top margin for the calendar section */
            }}>
              {/* Calendar wrapper - contains the calendar grid component */}
              <div className="calendar-wrapper" style={{
                width: '100%',
                overflowX: 'hidden', // Prevent horizontal scrolling
                padding: isMobile ? '12px 12px 0 12px' : '8px 12px 0 12px', /* Padding on sides and top, not bottom */
                boxSizing: 'border-box'
              }}>
                <Calendar
                  events={filteredEvents}
                  onEditEvent={(event) => {
                    setEventToEdit(event);
                  }}
                  onDeleteEvent={deleteAcceptedEvent}
                  onDateSelect={(date) => {
                    // Show loading indicator immediately when date is selected
                    setIsScrolling(true);

                    // Set the date and switch to Today tab
                    setSelectedDate(date);
                    setActiveTab('today');

                    // Hide loading after a delay to ensure rendering is complete
                    setTimeout(() => {
                      setIsScrolling(false);
                    }, 600);
                  }}
                  getEventsForDate={getEventsForDate}
                />
              </div>
            </div>
          </div>

          {/* Week Tab Content - shows only week view */}
          <div
            className={`tab-content week-tab ${activeTab === 'week' ? 'visible' : 'hidden'}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <div className="week-view-container" style={{
              width: '100%',
              marginTop: '12px'
            }}>
              <WeekView
                events={filteredEvents}
                getEventsForDate={getEventsForDate}
                getCategoryStyles={getCategoryStyles}
                onEditEvent={(event) => {
                  setEventToEdit(event);
                }}
                onDateSelect={(date, eventId, hour) => {
                  if (eventId) {
                    // If clicking on an event, switch to Today tab and scroll to event
                    setIsScrolling(true);
                    setSelectedDate(date);
                    setActiveTab('today');
                    
                    // Wait for tab switch and rendering, then scroll to specific event
                    setTimeout(() => {
                      setIsScrolling(false);
                      
                      // First ensure the correct day is visible
                      const selectedDayElement = document.querySelector('.day-events-group.selected-day');
                      const scrollContainer = scrollContainerRef.current;
                      
                      if (selectedDayElement && scrollContainer) {
                        // Scroll to the selected day first
                        const dayPosition = selectedDayElement.offsetTop;
                        scrollContainer.scrollTop = dayPosition - 40;
                        
                        // Then find and scroll to the specific event
                        setTimeout(() => {
                          const eventElement = document.querySelector(`[data-event-id="${eventId}"]`);
                          if (eventElement) {
                            // Use scrollIntoView for more reliable scrolling
                            eventElement.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'center',
                              inline: 'nearest' 
                            });
                            
                            // Add highlight effect to the event
                            eventElement.classList.add('event-highlight');
                            
                            // Click the event to expand it
                            setTimeout(() => {
                              eventElement.click();
                            }, 500); // Wait for scroll to complete
                            
                            // Remove highlight after animation
                            setTimeout(() => {
                              eventElement.classList.remove('event-highlight');
                            }, 2000);
                          } else {
                            console.warn(`Event element with id ${eventId} not found`);
                          }
                        }, 300); // Small delay to ensure day scroll completes
                      }
                    }, 800); // Slightly longer delay to ensure rendering is complete
                  } else if (hour !== undefined) {
                    // If clicking on an empty time slot, show create event modal
                    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    setCreateModalDate(dateStr);
                    setCreateModalHour(hour);
                    setShowCreateModal(true);
                  } else {
                    // Default behavior - switch to Today tab
                    setIsScrolling(true);
                    setSelectedDate(date);
                    setActiveTab('today');
                    setTimeout(() => {
                      setIsScrolling(false);
                    }, 600);
                  }
                }}
                isMobile={isMobile}
              />
            </div>
          </div>

          {/* Today Tab Content - shows events for selected date */}
          <div
            className={`tab-content today-tab ${activeTab === 'today' ? 'visible' : 'hidden'}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflowY: 'hidden',
              overflowX: 'hidden'
            }}
          >
            {renderDayEventsSection()}
          </div>
        </div>
      
      </div>

      {/* No events list shown in Month tab, just the calendar */}

      {/* Edit events are now handled via the /edit-event route */}
      
      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onAddEvent={acceptEvent}
        API_KEY={API_KEY}
        initialDate={createModalDate}
        initialHour={createModalHour}
      />
      
    </div>
  );
}

export default CalendarPage;